// src/pages/Admin/Members/AdminMemberProfilePreview.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { Trans } from "react-i18next";
import i18n from "@/i18n";
import api from "@/utils/axios";
import avatarPng from "@/assets/UI/avatar.png";

import GenderMaleIconMini from "@/components/icons/GenderMaleIconMini";
import GenderWomanIconMini from "@/components/icons/GenderWomanIconMini";
import GenderNonBinaryIconMini from "@/components/icons/GenderNonBinaryIconMini";
import GenderTransManIconMini from "@/components/icons/GenderTransManIconMini";
import GenderTransWomanIconMini from "@/components/icons/GenderTransWomanIconMini";
import GenderGenderfluidIconMini from "@/components/icons/GenderGenderfluidIconMini";
import GenderSelfDescribeIconMini from "@/components/icons/GenderSelfDescribeIconMini";

import {
  ATTRACTION_GENDERS,
  CONNECTION_TYPES,
  INTIMACY_TYPES,
  BODY_ATTRACT,
  SEXUAL_ENVIRONMENTS,
  SEXUAL_FANTASIES,
  EVENT_ATTRACT,
  BE_MATCHED,
} from "@/enums/lifestyleEnums";

// Reuse the same SCSS as MemberProfile
// Adjust this path if needed based on your folder structure
import cls from "@/pages/Lounge/Profile/MemberProfile/MemberProfile.module.scss";

const fadeIn = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
};

const GENDER_ICON_MAP = {
  man: GenderMaleIconMini,
  woman: GenderWomanIconMini,
  non_binary: GenderNonBinaryIconMini,
  trans_man: GenderTransManIconMini,
  trans_woman: GenderTransWomanIconMini,
  genderfluid: GenderGenderfluidIconMini,
  self_describe: GenderSelfDescribeIconMini,
  not_select: GenderSelfDescribeIconMini,
};

const SECONDARY_LANGUAGE_LABELS = {
  en: "English",
  fr: "Français",
  si: "සිංහල",
  ta: "தமிழ்",
  hi: "हिन्दी",
};

function getGenderIcon(gender) {
  return GENDER_ICON_MAP[gender] || GenderSelfDescribeIconMini;
}

function asArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value];
}

function formatRenewDay(renew) {
  if (!renew) return null;

  if (typeof renew === "number") {
    return i18n.t("memberProfile.member.memberRenewDayLabel", {
      day: renew,
    });
  }

  const d = new Date(renew);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  return String(renew);
}

const ATTRACTION_ORDER = ATTRACTION_GENDERS;

function buildAttractionNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = ATTRACTION_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.attraction.labels.${key}`);
    return (
      <span key={key} className={clsMod.hlb}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const CONNECTION_ORDER = CONNECTION_TYPES;

function buildConnectionNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = CONNECTION_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.connection.labels.${key}`);
    return (
      <span key={key} className={clsMod.hl}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const INTIMACY_ORDER = INTIMACY_TYPES;

function buildIntimacyNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = INTIMACY_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.intimacy.labels.${key}`);
    return (
      <span key={key} className={clsMod.hlb}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const BODY_ATTRACT_ORDER = BODY_ATTRACT;

function buildBodyAttractNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = BODY_ATTRACT_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.bodyAttract.labels.${key}`);
    return (
      <span key={key} className={clsMod.hlb}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const SEX_ENV_ORDER = SEXUAL_ENVIRONMENTS;

function buildSexEnvNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = SEX_ENV_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.sexualEnvironment.labels.${key}`);
    return (
      <span key={key} className={clsMod.hl}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const SEXUAL_FANTASY_ORDER = SEXUAL_FANTASIES;

function buildSexFantasyNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = SEXUAL_FANTASY_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.sexualFantasies.labels.${key}`);
    return (
      <span key={key} className={clsMod.hlb}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const EVENT_ATTRACT_ORDER = EVENT_ATTRACT;

function buildEventAttractNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = EVENT_ATTRACT_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.eventAttract.labels.${key}`);
    return (
      <span key={key} className={clsMod.hl}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const BE_MATCHED_ORDER = BE_MATCHED;

function buildBeMatchedNodes(keys, clsMod) {
  if (!keys || !keys.length) return null;

  const ordered = BE_MATCHED_ORDER.filter((k) => keys.includes(k));
  if (!ordered.length) return null;

  const parts = ordered.map((key) => {
    const label = i18n.t(`memberProfile.beMatched.labels.${key}`);
    return (
      <span key={key} className={clsMod.hlb}>
        {label}
      </span>
    );
  });

  const nodes = [];
  parts.forEach((node, idx) => {
    if (idx > 0 && idx === parts.length - 1) {
      nodes.push(<span key={`and-${idx}`}>, and </span>);
    } else if (idx > 0) {
      nodes.push(<span key={`comma-${idx}`}>, </span>);
    }
    nodes.push(node);
  });

  return nodes;
}

const AdminMemberProfilePreview = () => {
  const { userId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const { data } = await api.get(
          `/v1/admin/members/single/profile-preview/${userId}`,
        );
        if (!ignore) {
          setProfile(data?.member || null);
        }
      } catch (err) {
        console.error(err);
        if (!ignore) {
          setError(
            err?.response?.data?.message ||
              i18n.t("memberProfile.states.error"),
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [userId]);

  const avatarSrc = profile?.avatar?.url || avatarPng;

  const memberType = profile?.memberType || "single";
  const isCouple = memberType === "couple";

  // If you later include username from backend, use that here
  const memberName = useMemo(() => {
    if (profile?.username) return profile.username;
    return i18n.t("memberProfile.fallback.name");
  }, [profile?.username]);

  const renewLabel = formatRenewDay(profile?.member_renew_day);

  const memberLifestyle = profile?.lifestyle || null;
  const partner = profile?.primaryPartner || null;
  const partnerLifestyle = partner?.lifestyle || null;

  // ===== Gender badge / icon logic =====
  const rawGender = profile?.personal?.gender || "not_select";
  const rawPartnerGender = partner?.personal?.gender || null;

  const GenderIcon = getGenderIcon(rawGender);
  const PartnerGenderIcon = rawPartnerGender
    ? getGenderIcon(rawPartnerGender)
    : null;

  // ===== Description helpers =====
  const genderKey = rawGender || "not_select";
  const partnerGenderKey = rawPartnerGender || "not_select";

  const genderPhrase = i18n.t(`memberProfile.description.gender.${genderKey}`);
  const partnerGenderPhrase = i18n.t(
    `memberProfile.description.gender.${partnerGenderKey}`,
  );

  const city = profile?.personal?.city || null;
  const country = profile?.personal?.country || null;
  const cityCountry =
    city && country ? `${city}, ${country}` : city || country || null;

  const secondaryLanguageCode = profile?.secondaryLanguage || null;

  const secondaryLanguageLabel =
    secondaryLanguageCode &&
    (SECONDARY_LANGUAGE_LABELS[secondaryLanguageCode] || secondaryLanguageCode);

  const singleLanguageFragment = secondaryLanguageLabel
    ? i18n.t("memberProfile.description.single.languageFragment", {
        language: secondaryLanguageLabel,
      })
    : "";

  const coupleLanguageFragment = secondaryLanguageLabel
    ? i18n.t("memberProfile.description.couple.languageFragment", {
        language: secondaryLanguageLabel,
      })
    : "";

  const relationshipTypeKey = partner?.relationshipType || null;
  const relationshipStatusKey = partner?.relationshipStatus || null;

  const relationshipTypePhrase = relationshipTypeKey
    ? i18n.t(
        `memberProfile.description.relationshipType.${relationshipTypeKey}`,
      )
    : i18n.t("memberProfile.description.relationshipType.default");

  const relationshipStatusPhrase = relationshipStatusKey
    ? i18n.t(
        `memberProfile.description.relationshipStatus.${relationshipStatusKey}`,
      )
    : i18n.t("memberProfile.description.relationshipStatus.default");

  const hasLocation = !!cityCountry;

  const descriptionKey = isCouple
    ? hasLocation
      ? "memberProfile.description.couple.withLocation"
      : "memberProfile.description.couple.noLocation"
    : hasLocation
      ? "memberProfile.description.single.withLocation"
      : "memberProfile.description.single.noLocation";

  const descriptionValues = isCouple
    ? {
        relationshipTypePhrase,
        relationshipStatusPhrase,
        cityCountry: cityCountry || "",
        languageFragment: coupleLanguageFragment || "",
        genderPhrase,
        partnerGenderPhrase,
      }
    : {
        genderPhrase,
        cityCountry: cityCountry || "",
        languageFragment: singleLanguageFragment || "",
      };

  // ===== Sexual identity & role description helpers =====
  const memberSexualIdentityKey =
    memberLifestyle?.sexual_identity || "not_select";
  const memberSexualRoleKey = memberLifestyle?.sexual_role || "not_select";

  const memberIdentityLabel = i18n.t(
    `memberProfile.sexualDescription.identityLabel.${memberSexualIdentityKey}`,
  );
  const memberIdentityText = i18n.t(
    `memberProfile.sexualDescription.identityText.${memberSexualIdentityKey}`,
  );
  const memberRoleLabel = i18n.t(
    `memberProfile.sexualDescription.roleLabel.${memberSexualRoleKey}`,
  );
  const memberRoleText = i18n.t(
    `memberProfile.sexualDescription.roleText.${memberSexualRoleKey}`,
  );

  const partnerSexualIdentityKey =
    partnerLifestyle?.sexual_identity || "not_select";
  const partnerSexualRoleKey = partnerLifestyle?.sexual_role || "not_select";

  const partnerIdentityLabel = i18n.t(
    `memberProfile.sexualDescription.identityLabel.${partnerSexualIdentityKey}`,
  );
  const partnerIdentityText = i18n.t(
    `memberProfile.sexualDescription.identityText.${partnerSexualIdentityKey}`,
  );
  const partnerRoleLabel = i18n.t(
    `memberProfile.sexualDescription.roleLabel.${partnerSexualRoleKey}`,
  );
  const partnerRoleText = i18n.t(
    `memberProfile.sexualDescription.roleText.${partnerSexualRoleKey}`,
  );

  const hasMemberSexualData =
    memberSexualIdentityKey !== "not_select" ||
    memberSexualRoleKey !== "not_select";

  const hasPartnerSexualData =
    isCouple &&
    partner &&
    partnerLifestyle &&
    (partnerSexualIdentityKey !== "not_select" ||
      partnerSexualRoleKey !== "not_select");

  // ===== Attraction genders helpers =====
  const memberAttractionKeys = asArray(memberLifestyle?.attraction_genders);
  const partnerAttractionKeys = asArray(partnerLifestyle?.attraction_genders);

  const hasMemberAttraction = memberAttractionKeys.length > 0;
  const hasPartnerAttraction = partnerAttractionKeys.length > 0;

  const memberAttractionNodes = buildAttractionNodes(memberAttractionKeys, cls);
  const partnerAttractionNodes = buildAttractionNodes(
    partnerAttractionKeys,
    cls,
  );

  // ===== Connection types helpers =====
  const memberConnectionKeys = asArray(memberLifestyle?.connection_types);
  const partnerConnectionKeys = asArray(partnerLifestyle?.connection_types);

  const hasMemberConnection = memberConnectionKeys.length > 0;
  const hasPartnerConnection = partnerConnectionKeys.length > 0;

  const memberConnectionNodes = buildConnectionNodes(memberConnectionKeys, cls);
  const partnerConnectionNodes = buildConnectionNodes(
    partnerConnectionKeys,
    cls,
  );

  // ===== Intimacy types helpers =====
  const memberIntimacyKeys = asArray(memberLifestyle?.intimacy_types);
  const partnerIntimacyKeys = asArray(partnerLifestyle?.intimacy_types);

  const hasMemberIntimacy = memberIntimacyKeys.length > 0;
  const hasPartnerIntimacy = partnerIntimacyKeys.length > 0;

  const memberIntimacyNodes = buildIntimacyNodes(memberIntimacyKeys, cls);
  const partnerIntimacyNodes = buildIntimacyNodes(partnerIntimacyKeys, cls);

  // ===== Body type helpers =====
  const memberBodyKey = memberLifestyle?.body_type || null;
  const partnerBodyKey = partnerLifestyle?.body_type || null;

  const hasMemberBodyData = !!memberBodyKey;
  const hasPartnerBodyData = !!partnerBodyKey;

  const memberBodyLabel = memberBodyKey
    ? i18n.t(`memberProfile.bodyDescription.labels.${memberBodyKey}`)
    : "";

  const memberBodyText = memberBodyKey
    ? i18n.t(`memberProfile.bodyDescription.text.${memberBodyKey}`)
    : "";

  const partnerBodyLabel = partnerBodyKey
    ? i18n.t(`memberProfile.bodyDescription.labels.${partnerBodyKey}`)
    : "";

  const partnerBodyText = partnerBodyKey
    ? i18n.t(`memberProfile.bodyDescription.text.${partnerBodyKey}`)
    : "";

  // ===== Body-attraction helpers =====
  const memberBodyAttractKeys = asArray(memberLifestyle?.body_attract);
  const partnerBodyAttractKeys = asArray(partnerLifestyle?.body_attract);

  const hasMemberBodyAttract = memberBodyAttractKeys.length > 0;
  const hasPartnerBodyAttract = partnerBodyAttractKeys.length > 0;

  const memberBodyAttractNodes = buildBodyAttractNodes(
    memberBodyAttractKeys,
    cls,
  );
  const partnerBodyAttractNodes = buildBodyAttractNodes(
    partnerBodyAttractKeys,
    cls,
  );

  // ===== Sexual environment helpers =====
  const memberSexEnvKeys = asArray(memberLifestyle?.sexual_environment);
  const partnerSexEnvKeys = asArray(partnerLifestyle?.sexual_environment);

  const hasMemberSexEnv = memberSexEnvKeys.length > 0;
  const hasPartnerSexEnv = partnerSexEnvKeys.length > 0;

  const memberSexEnvNodes = buildSexEnvNodes(memberSexEnvKeys, cls);
  const partnerSexEnvNodes = buildSexEnvNodes(partnerSexEnvKeys, cls);

  // ===== Sexual fantasies helpers =====
  const memberFantasyKeys = asArray(memberLifestyle?.sexual_fantasies);
  const partnerFantasyKeys = asArray(partnerLifestyle?.sexual_fantasies);

  const hasMemberFantasies = memberFantasyKeys.length > 0;
  const hasPartnerFantasies = partnerFantasyKeys.length > 0;

  const memberFantasyNodes = buildSexFantasyNodes(memberFantasyKeys, cls);
  const partnerFantasyNodes = buildSexFantasyNodes(partnerFantasyKeys, cls);

  // ===== Event attract helpers =====
  const memberEventKeys = asArray(memberLifestyle?.event_attract);
  const hasMemberEventAttract = memberEventKeys.length > 0;

  const memberEventAttractNodes = buildEventAttractNodes(memberEventKeys, cls);

  // ===== Be-matched helpers =====
  const memberBeMatchedKeys = asArray(memberLifestyle?.be_matched);
  const hasMemberBeMatched = memberBeMatchedKeys.length > 0;

  const memberBeMatchedNodes = buildBeMatchedNodes(memberBeMatchedKeys, cls);

  // ===== Alcohol & cannabis helpers =====
  const memberAlcoholKey = memberLifestyle?.alcohol_cannabis || null;
  const partnerAlcoholKey = partnerLifestyle?.alcohol_cannabis || null;

  const hasMemberAlcoholData = !!memberAlcoholKey;
  const hasPartnerAlcoholData = !!partnerAlcoholKey;

  const memberAlcoholLabel = memberAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.labels.${memberAlcoholKey}`)
    : "";

  const memberAlcoholText = memberAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.text.${memberAlcoholKey}`)
    : "";

  const partnerAlcoholLabel = partnerAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.labels.${partnerAlcoholKey}`)
    : "";

  const partnerAlcoholText = partnerAlcoholKey
    ? i18n.t(`memberProfile.alcoholCannabis.text.${partnerAlcoholKey}`)
    : "";

  return (
    <div className={cls.profilePage}>
      {/* Header */}
      <Motion.header
        className={cls.header}
        variants={fadeIn}
        initial="hidden"
        animate="show"
      >
        <div className={cls.headerMain}>
          <span
            className={`${cls.avatarShell} ${
              isCouple ? cls["avatarShell--couple"] : ""
            }`}
          >
            <span className="avatar-wrap">
              <img src={avatarSrc} alt={memberName} className={cls.avatar} />
            </span>

            {loading && (
              <span className={cls.avatarSpinner} aria-hidden="true" />
            )}

            {isCouple ? (
              <span
                className={`${cls.genderBadge} ${cls.genderBadgePair}`}
                aria-hidden="true"
                title="Couple"
              >
                <span className={cls.genderBadgeItem}>
                  <GenderIcon size={14} />
                </span>
                {PartnerGenderIcon && (
                  <span className={cls.genderBadgeItem}>
                    <PartnerGenderIcon size={14} />
                  </span>
                )}
              </span>
            ) : (
              <span
                className={cls.genderBadge}
                aria-hidden="true"
                title="Gender"
              >
                <GenderIcon size={16} />
              </span>
            )}
          </span>

          <div className={cls.headerText}>
            <h1 className={cls.title}>
              {i18n.t("memberProfile.title")}{" "}
              <span className={cls.adminTag}>
                {/* Optional: small label to remind this is admin view */}
                {i18n.t("memberProfile.adminPreviewLabel", "Admin preview")}
              </span>
            </h1>
            <p className={cls.subtitle}>
              {i18n.t("memberProfile.subtitle", { name: memberName })}
            </p>

            <div className={cls.pillRow}>
              <span className={cls.pill}>
                {i18n.t(`memberProfile.member.memberType.${memberType}`)}
              </span>

              {profile?.personal?.country && (
                <span className={cls.pill}>
                  {profile.personal.city
                    ? `${profile.personal.city}, ${profile.personal.country}`
                    : profile.personal.country}
                </span>
              )}

              {renewLabel && <span className={cls.pill}>{renewLabel}</span>}
            </div>
            <Link
              to={`/admin/members/single/${userId}`}
              className={cls.backBtn}
            >
              ← Back to Member
            </Link>
          </div>
        </div>
      </Motion.header>

      {/* States */}
      {loading && (
        <div className={cls.stateBox}>
          <span className={cls.loader} aria-hidden="true" />
          <span className={cls.stateText}>
            {i18n.t("memberProfile.states.loading")}
          </span>
        </div>
      )}

      {!loading && error && (
        <div className={cls.stateBoxError} role="alert">
          <span className={cls.stateText}>{error}</span>
          <button
            type="button"
            className={cls.retryBtn}
            onClick={() => window.location.reload()}
          >
            {i18n.t("memberProfile.states.retry")}
          </button>
        </div>
      )}

      {!loading && !error && profile && (
        <Motion.div
          className={cls.grid}
          variants={fadeIn}
          initial="hidden"
          animate="show"
        >
          <section className={`${cls.card} ${cls.cardMember}`}>
            <div className={cls.cardBody}>
              {/* Description (with highlight spans) */}
              {descriptionKey && (
                <div className={`${cls.block} ${cls.blockDescription}`}>
                  <p className={cls.descriptionText}>
                    <Trans
                      i18nKey={descriptionKey}
                      values={descriptionValues}
                      components={{
                        hl: <span className={cls.hl} />,
                        hlb: <span className={cls.hlb} />,
                      }}
                    />
                  </p>
                </div>
              )}

              {/* Meta row: member + partner */}
              <div className={cls.metaGrid}>
                {/* Sexual identity & role */}
                <div className={`${cls.block} ${cls.blockIntimacy}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.sexualDescription.titleCouple")
                      : i18n.t("memberProfile.sexualDescription.titleSingle")}
                  </h3>

                  {!memberLifestyle || !hasMemberSexualData ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.sexualDescription.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerSexualData ? (
                    <p className={cls.sexDescription}>
                      <Trans
                        i18nKey="memberProfile.sexualDescription.single.line1"
                        values={{
                          identityLabel: memberIdentityLabel,
                          identityText: memberIdentityText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                      <br />
                      <Trans
                        i18nKey="memberProfile.sexualDescription.single.line2"
                        values={{
                          roleLabel: memberRoleLabel,
                          roleText: memberRoleText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                          hlb: <span className={cls.hlb} />,
                        }}
                      />
                    </p>
                  ) : (
                    <>
                      <p className={cls.sexDescription}>
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.memberLine1"
                          values={{
                            identityLabel: memberIdentityLabel,
                            identityText: memberIdentityText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                        <br />
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.memberLine2"
                          values={{
                            roleLabel: memberRoleLabel,
                            roleText: memberRoleText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                      </p>

                      <p className={cls.sexDescription}>
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.partnerLine1"
                          values={{
                            identityLabel: partnerIdentityLabel,
                            identityText: partnerIdentityText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                        <br />
                        <Trans
                          i18nKey="memberProfile.sexualDescription.couple.partnerLine2"
                          values={{
                            roleLabel: partnerRoleLabel,
                            roleText: partnerRoleText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                            hlb: <span className={cls.hlb} />,
                          }}
                        />
                      </p>
                    </>
                  )}
                </div>

                {/* Attraction genders */}
                <div className={`${cls.block} ${cls.blockAttraction}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.attraction.titleCouple")
                      : i18n.t("memberProfile.attraction.titleSingle")}
                  </h3>

                  {!hasMemberAttraction ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.attraction.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerAttraction ? (
                    <p className={cls.attrDescription}>
                      {i18n.t("memberProfile.attraction.single.prefix")}
                      {memberAttractionNodes}
                      {i18n.t("memberProfile.attraction.single.suffix")}
                    </p>
                  ) : (
                    <>
                      <p className={cls.attrDescription}>
                        {i18n.t("memberProfile.attraction.couple.memberPrefix")}
                        {memberAttractionNodes}
                        {i18n.t("memberProfile.attraction.couple.suffix")}
                      </p>

                      <p className={cls.attrDescription}>
                        {i18n.t(
                          "memberProfile.attraction.couple.partnerPrefix",
                        )}
                        {partnerAttractionNodes}
                        {i18n.t("memberProfile.attraction.couple.suffix")}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Connection + Intimacy */}
              <div className={cls.metaGridSecondary}>
                <section
                  className={`${cls.metaPanel} ${cls.metaPanelConnection}`}
                >
                  <h3 className={cls.metaPanelTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.connection.titleCouple")
                      : i18n.t("memberProfile.connection.titleSingle")}
                  </h3>

                  {!hasMemberConnection ? (
                    <p className={cls.metaPanelEmpty}>
                      {i18n.t("memberProfile.connection.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerConnection ? (
                    <p className={cls.connDescription}>
                      {i18n.t("memberProfile.connection.single.prefix")}
                      {memberConnectionNodes}
                      {i18n.t("memberProfile.connection.single.suffix")}
                    </p>
                  ) : (
                    <>
                      <p className={cls.connDescription}>
                        {i18n.t("memberProfile.connection.couple.memberPrefix")}
                        {memberConnectionNodes}
                        {i18n.t("memberProfile.connection.couple.suffix")}
                      </p>

                      <p className={cls.connDescription}>
                        {i18n.t(
                          "memberProfile.connection.couple.partnerPrefix",
                        )}
                        {partnerConnectionNodes}
                        {i18n.t("memberProfile.connection.couple.suffix")}
                      </p>
                    </>
                  )}
                </section>

                <section
                  className={`${cls.metaPanel} ${cls.metaPanelIntimacy}`}
                >
                  <h3 className={cls.metaPanelTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.intimacy.titleCouple")
                      : i18n.t("memberProfile.intimacy.titleSingle")}
                  </h3>

                  {!hasMemberIntimacy ? (
                    <p className={cls.metaPanelEmpty}>
                      {i18n.t("memberProfile.intimacy.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerIntimacy ? (
                    <p className={cls.intimacyDescription}>
                      {i18n.t("memberProfile.intimacy.single.prefix")}
                      {memberIntimacyNodes}
                      {i18n.t("memberProfile.intimacy.single.suffix")}
                    </p>
                  ) : (
                    <>
                      <p className={cls.intimacyDescription}>
                        {i18n.t("memberProfile.intimacy.couple.memberPrefix")}
                        {memberIntimacyNodes}
                        {i18n.t("memberProfile.intimacy.couple.suffix")}
                      </p>

                      <p className={cls.intimacyDescription}>
                        {i18n.t("memberProfile.intimacy.couple.partnerPrefix")}
                        {partnerIntimacyNodes}
                        {i18n.t("memberProfile.intimacy.couple.suffix")}
                      </p>
                    </>
                  )}
                </section>
              </div>

              {/* Body + Body attraction */}
              <div className={cls.metaGrid}>
                <div className={`${cls.block} ${cls.blockBody}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.bodyDescription.titleCouple")
                      : i18n.t("memberProfile.bodyDescription.titleSingle")}
                  </h3>

                  {!memberLifestyle || !hasMemberBodyData ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.bodyDescription.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerBodyData ? (
                    <p className={cls.bodyDescription}>
                      <Trans
                        i18nKey="memberProfile.bodyDescription.single.line1"
                        values={{
                          bodyLabel: memberBodyLabel,
                          bodyText: memberBodyText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                        }}
                      />
                    </p>
                  ) : (
                    <>
                      <p className={cls.bodyDescription}>
                        <Trans
                          i18nKey="memberProfile.bodyDescription.couple.memberLine"
                          values={{
                            bodyLabel: memberBodyLabel,
                            bodyText: memberBodyText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                          }}
                        />
                      </p>

                      <p className={cls.bodyDescription}>
                        <Trans
                          i18nKey="memberProfile.bodyDescription.couple.partnerLine"
                          values={{
                            bodyLabel: partnerBodyLabel,
                            bodyText: partnerBodyText,
                          }}
                          components={{
                            hl: <span className={cls.hl} />,
                          }}
                        />
                      </p>
                    </>
                  )}
                </div>

                <div className={`${cls.block} ${cls.blockBodyAttract}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.bodyAttract.titleCouple")
                      : i18n.t("memberProfile.bodyAttract.titleSingle")}
                  </h3>

                  {!hasMemberBodyAttract ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.bodyAttract.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerBodyAttract ? (
                    <p className={cls.bodyAttractDescription}>
                      {i18n.t("memberProfile.bodyAttract.single.prefix")}
                      {memberBodyAttractNodes}
                      {i18n.t("memberProfile.bodyAttract.single.suffix")}
                    </p>
                  ) : (
                    <>
                      <p className={cls.bodyAttractDescription}>
                        {i18n.t(
                          "memberProfile.bodyAttract.couple.memberPrefix",
                        )}
                        {memberBodyAttractNodes}
                        {i18n.t("memberProfile.bodyAttract.couple.suffix")}
                      </p>

                      <p className={cls.bodyAttractDescription}>
                        {i18n.t(
                          "memberProfile.bodyAttract.couple.partnerPrefix",
                        )}
                        {partnerBodyAttractNodes}
                        {i18n.t("memberProfile.bodyAttract.couple.suffix")}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Sex environments + fantasies */}
              <div className={cls.metaGrid}>
                <div className={`${cls.block} ${cls.blockSexEnv}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.sexualEnvironment.titleCouple")
                      : i18n.t("memberProfile.sexualEnvironment.titleSingle")}
                  </h3>

                  {!hasMemberSexEnv ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.sexualEnvironment.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerSexEnv ? (
                    <p className={cls.sexEnvDescription}>
                      {i18n.t("memberProfile.sexualEnvironment.single.prefix")}
                      {memberSexEnvNodes}
                      {i18n.t("memberProfile.sexualEnvironment.single.suffix")}
                    </p>
                  ) : (
                    <>
                      <p className={cls.sexEnvDescription}>
                        {i18n.t(
                          "memberProfile.sexualEnvironment.couple.memberPrefix",
                        )}
                        {memberSexEnvNodes}
                        {i18n.t(
                          "memberProfile.sexualEnvironment.couple.suffix",
                        )}
                      </p>

                      <p className={cls.sexEnvDescription}>
                        {i18n.t(
                          "memberProfile.sexualEnvironment.couple.partnerPrefix",
                        )}
                        {partnerSexEnvNodes}
                        {i18n.t(
                          "memberProfile.sexualEnvironment.couple.suffix",
                        )}
                      </p>
                    </>
                  )}
                </div>

                <div className={`${cls.block} ${cls.blockFantasies}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.sexualFantasies.titleCouple")
                      : i18n.t("memberProfile.sexualFantasies.titleSingle")}
                  </h3>

                  {!hasMemberFantasies ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.sexualFantasies.empty")}
                    </p>
                  ) : !isCouple || !hasPartnerFantasies ? (
                    <p className={cls.sexFantasyDescription}>
                      {i18n.t("memberProfile.sexualFantasies.single.prefix")}
                      {memberFantasyNodes}
                      {i18n.t("memberProfile.sexualFantasies.single.suffix")}
                    </p>
                  ) : (
                    <>
                      <p className={cls.sexFantasyDescription}>
                        {i18n.t(
                          "memberProfile.sexualFantasies.couple.memberPrefix",
                        )}
                        {memberFantasyNodes}
                        {i18n.t("memberProfile.sexualFantasies.couple.suffix")}
                      </p>

                      <p className={cls.sexFantasyDescription}>
                        {i18n.t(
                          "memberProfile.sexualFantasies.couple.partnerPrefix",
                        )}
                        {partnerFantasyNodes}
                        {i18n.t("memberProfile.sexualFantasies.couple.suffix")}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Event attract + be-matched */}
              <div className={cls.metaGrid}>
                <div className={`${cls.block} ${cls.blockEventAttract}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.eventAttract.titleCouple")
                      : i18n.t("memberProfile.eventAttract.titleSingle")}
                  </h3>

                  {!hasMemberEventAttract ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.eventAttract.empty")}
                    </p>
                  ) : (
                    <p className={cls.eventAttractDescription}>
                      {isCouple
                        ? i18n.t("memberProfile.eventAttract.couple.prefix")
                        : i18n.t("memberProfile.eventAttract.single.prefix")}
                      {memberEventAttractNodes}
                      {isCouple
                        ? i18n.t("memberProfile.eventAttract.couple.suffix")
                        : i18n.t("memberProfile.eventAttract.single.suffix")}
                    </p>
                  )}
                </div>

                <div className={`${cls.block} ${cls.blockBeMatched}`}>
                  <h3 className={cls.blockTitle}>
                    {isCouple
                      ? i18n.t("memberProfile.beMatched.titleCouple")
                      : i18n.t("memberProfile.beMatched.titleSingle")}
                  </h3>

                  {!hasMemberBeMatched ? (
                    <p className={cls.emptyText}>
                      {i18n.t("memberProfile.beMatched.empty")}
                    </p>
                  ) : (
                    <p className={cls.beMatchedDescription}>
                      {isCouple
                        ? i18n.t("memberProfile.beMatched.couple.prefix")
                        : i18n.t("memberProfile.beMatched.single.prefix")}
                      {memberBeMatchedNodes}
                      {isCouple
                        ? i18n.t("memberProfile.beMatched.couple.suffix")
                        : i18n.t("memberProfile.beMatched.single.suffix")}
                    </p>
                  )}
                </div>
              </div>

              {/* Alcohol & cannabis */}
              <div className={`${cls.block} ${cls.blockAlcohol}`}>
                <h3 className={cls.blockTitle}>
                  {isCouple
                    ? i18n.t("memberProfile.alcoholCannabis.titleCouple")
                    : i18n.t("memberProfile.alcoholCannabis.titleSingle")}
                </h3>

                {!memberLifestyle || !hasMemberAlcoholData ? (
                  <p className={cls.emptyText}>
                    {i18n.t("memberProfile.alcoholCannabis.empty")}
                  </p>
                ) : !isCouple || !hasPartnerAlcoholData ? (
                  <p className={cls.alcoholText}>
                    <Trans
                      i18nKey="memberProfile.alcoholCannabis.single.line1"
                      values={{
                        label: memberAlcoholLabel,
                        text: memberAlcoholText,
                      }}
                      components={{
                        hl: <span className={cls.hl} />,
                      }}
                    />
                  </p>
                ) : (
                  <>
                    <p className={cls.alcoholText}>
                      <Trans
                        i18nKey="memberProfile.alcoholCannabis.couple.memberLine"
                        values={{
                          label: memberAlcoholLabel,
                          text: memberAlcoholText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                        }}
                      />
                    </p>

                    <p className={cls.alcoholText}>
                      <Trans
                        i18nKey="memberProfile.alcoholCannabis.couple.partnerLine"
                        values={{
                          label: partnerAlcoholLabel,
                          text: partnerAlcoholText,
                        }}
                        components={{
                          hl: <span className={cls.hl} />,
                        }}
                      />
                    </p>
                  </>
                )}
              </div>
            </div>
          </section>
        </Motion.div>
      )}
    </div>
  );
};

export default AdminMemberProfilePreview;
